<script setup>
import { computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  agents: { type: Array, default: () => [] },
  conversations: { type: Array, default: () => [] },
  selectedAgentId: { type: [String, Number], default: null },
  selectedConversationId: { type: [String, Number], default: null },
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'select-agent',
  'select-conversation',
  'new-conversation',
]);

const activeAgent = computed(() =>
  props.agents.find(a => String(a.id) === String(props.selectedAgentId))
);

const getAgentInitials = name => {
  return name
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
};

const formatTime = dateStr => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  if (isToday) {
    return d.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};
</script>

<template>
  <div class="flex h-full flex-col bg-n-background">
    <div
      class="flex items-center justify-between border-b border-n-weak px-4 py-3"
    >
      <h3 class="text-base font-semibold text-n-slate-12">IA Chat</h3>
    </div>

    <div class="flex gap-1 overflow-x-auto border-b border-n-weak px-3 py-2">
      <button
        v-for="agent in agents"
        :key="agent.id"
        class="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-all duration-150"
        :class="
          String(agent.id) === String(selectedAgentId)
            ? 'bg-n-alpha-2 font-medium text-n-slate-12'
            : 'text-n-slate-11 hover:bg-n-alpha-1'
        "
        @click="emit('select-agent', agent.id)"
      >
        <div
          class="flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white"
          :class="
            agent.active
              ? 'bg-gradient-to-br from-violet-500 to-indigo-600'
              : 'bg-n-slate-8'
          "
        >
          {{ getAgentInitials(agent.name) }}
        </div>
        <span class="truncate">{{ agent.name }}</span>
        <span
          v-if="agent.active"
          class="size-1.5 shrink-0 rounded-full bg-emerald-500"
        />
      </button>
    </div>

    <div v-if="activeAgent" class="flex flex-1 flex-col overflow-hidden">
      <div class="flex items-center justify-between px-4 py-2">
        <span class="text-xs font-medium uppercase tracking-wide text-n-slate-10"
          >Conversas</span
        >
        <Button
          size="xs"
          variant="ghost"
          color-scheme="secondary"
          icon="i-lucide-plus"
          @click="emit('new-conversation')"
        />
      </div>

      <div class="flex-1 overflow-y-auto">
        <div
          v-if="isLoading"
          class="flex items-center justify-center py-8"
        >
          <span class="i-lucide-loader-2 animate-spin text-xl text-n-slate-10" />
        </div>

        <div v-else-if="conversations.length === 0" class="px-4 py-8 text-center">
          <p class="text-sm text-n-slate-10">Nenhuma conversa ainda.</p>
          <button
            class="mt-2 text-sm font-medium text-n-blue-11 hover:text-n-blue-12"
            @click="emit('new-conversation')"
          >
            Iniciar conversa
          </button>
        </div>

        <button
          v-for="conv in conversations"
          v-else
          :key="conv.id"
          class="flex w-full items-center gap-3 border-b border-n-weak px-4 py-3 text-left transition-colors duration-100"
          :class="
            String(conv.id) === String(selectedConversationId)
              ? 'bg-n-alpha-2'
              : 'hover:bg-n-alpha-1'
          "
          @click="emit('select-conversation', conv.id)"
        >
          <div class="flex min-w-0 flex-1 flex-col gap-0.5">
            <span class="truncate text-sm font-medium text-n-slate-12">
              {{ conv.title || 'Nova conversa' }}
            </span>
            <span
              v-if="conv.last_message"
              class="truncate text-xs text-n-slate-10"
            >
              {{ conv.last_message.content }}
            </span>
          </div>
          <span
            v-if="conv.last_message"
            class="shrink-0 text-[10px] text-n-slate-9"
          >
            {{ formatTime(conv.last_message.created_at) }}
          </span>
        </button>
      </div>
    </div>

    <div
      v-else
      class="flex flex-1 flex-col items-center justify-center gap-2 px-4 text-center"
    >
      <span
        v-if="!isLoading"
        class="text-sm text-n-slate-10"
      >
        Selecione um agente acima
      </span>
      <span
        v-else
        class="i-lucide-loader-2 animate-spin text-xl text-n-slate-10"
      />
    </div>
  </div>
</template>
